import React, { useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import safePlay from './utils/safePlay';

const VideoPlayer = forwardRef(({ src, isActive, isMuted, onPlayingChange = () => { }, onEnded = () => { }, onNearEnd = () => { }, pausedIntentRef, index }, ref) => {
    const localRef = useRef(null);
    const firedRef = useRef(false);

    useEffect(() => {
        const el = localRef.current;
        if (!el) return undefined;

        const updatePlaying = () => onPlayingChange(!el.paused);
        const onPlay = () => { firedRef.current = false; updatePlaying(); };
        const onPlaying = () => updatePlaying();
        const onPause = () => updatePlaying();
        const onCanPlay = () => updatePlaying();
        const onLoaded = () => updatePlaying();

        const onTimeUpdate = () => {
            try {
                if (!el || !el.duration || el.duration === Infinity) return;
                const remaining = el.duration - el.currentTime;
                if (remaining <= 0.25 && !firedRef.current) {
                    firedRef.current = true;
                    onNearEnd();
                }
            } catch (e) { /* ignore */ }
        };

        const onEndedLocal = () => {
            onEnded();
            firedRef.current = true;
        };

        el.addEventListener('play', onPlay);
        el.addEventListener('playing', onPlaying);
        el.addEventListener('pause', onPause);
        el.addEventListener('canplay', onCanPlay);
        el.addEventListener('loadedmetadata', onLoaded);
        el.addEventListener('timeupdate', onTimeUpdate);
        el.addEventListener('ended', onEndedLocal);

        el.muted = !!isMuted;

        // If this slide became active, try to auto-play unless user explicitly paused
        const userPaused = !!(pausedIntentRef && pausedIntentRef.current && pausedIntentRef.current[index]);
        if (isActive && !userPaused) {
            safePlay(el).catch(() => { });
        }

        return () => {
            el.removeEventListener('play', onPlay);
            el.removeEventListener('playing', onPlaying);
            el.removeEventListener('pause', onPause);
            el.removeEventListener('canplay', onCanPlay);
            el.removeEventListener('loadedmetadata', onLoaded);
            el.removeEventListener('timeupdate', onTimeUpdate);
            el.removeEventListener('ended', onEndedLocal);
        };
    }, [isActive, isMuted, src]);

    useImperativeHandle(ref, () => ({
        play: () => {
            const el = localRef.current;
            if (!el) return Promise.reject(new Error('no-el'));
            return safePlay(el);
        },
        pause: () => {
            const el = localRef.current;
            if (!el) return;
            try { el.pause(); } catch (e) { }
        },
        setMuted: (m) => {
            const el = localRef.current;
            if (!el) return;
            el.muted = !!m;
        },
        isPaused: () => {
            const el = localRef.current;
            return !el ? true : el.paused;
        }
    }));

    // Keep the actual video element muted state in sync with prop
    useEffect(() => {
        const el = localRef.current;
        if (!el) return;
        try { el.muted = !!isMuted; } catch (e) { /* ignore */ }
    }, [isMuted]);

    // Expose controls via ref if needed
    return (
        <video
            ref={localRef}
            className="carousel-media"
            src={src}
            playsInline
            loop
            muted={!!isMuted}
            preload="metadata"
        />
    );
});

export default VideoPlayer;
